export const meta = {
  id: '30-passive-voice',
  order: 30,
  checkFocus: { voice: ['passive'] },   // what the end-of-lesson check tests first
  titleFi: 'Passiivi',
  titleEn: 'Passive voice',
  duolingoUnit: 'duo-35-passive-voice',
  lemmaIds: ['melvo', 'ondoso', 'ondossi', 'ours:ossēnnon', 'ours:ōdritty', 'ours:idakonon', 'ours:laodinon', 'ours:ozbirnon', 'ours:dēdenon', 'ours:dēdelītsos', 'ours:rhovītsos', 'ours:vestretruragon', 'ours:pālektys', 'ours:iqighilla', 'ours:nābēdir', 'ours:azanduragon', 'ours:ospȳnnuragon', 'ours:irūnon', 'ours:vagābnon', 'ours:joribardugon', 'ours:pirtruragon'],
  exampleIds: [
    'ex-8b8dd815ed81',
    'hotd-s1e02-012',
    'hotd-s1e10-007',
    'hotd-s2e07-003',
    'ex-53eea3e8d701',
    'ex-88132a27c24f',
    'ex-848daf1f9434',
    'ex-842e348ef160',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Passive_voice',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Passive',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Conjugation#Passive',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Adpositions',
    'https://wiki.languageinvention.com/index.php?title=ondoso',
    'https://wiki.languageinvention.com/index.php?title=ondossi',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['passive'],
  drills: { voice: ['passive', 'mediopassive'] },
  showLines: ['hotd-s1e02-012', 'hotd-s1e10-007', 'hotd-s2e07-003'],
  pronunciation: { focus: 'long-vowels', words: ['Zaldrījudirys', 'īlilion', 'Ghīs', 'nāvar', 'Mȳr'] },
  letter: {
    fi: 'Kirjoita kolme lausetta, joissa tekijä jää pois (passiivi).',
    en: 'Write three sentences in which the doer is left out (passive).',
  },
  scene: 'hotd-s1e10-1',
}

# Passiivi

Passiivi nostaa <Term id="transitiivinen">transitiiviverbin</Term> objektin etualalle ja siirtää tekijän taka-alalle. Englannissa tyypillinen esimerkki on *The pear was eaten (by the man)*: päärynä on lauseen aihe, ja mies mainitaan vain, jos niin halutaan. Korkeavalyriassa passiivi muodostetaan verbin omalla taivutusmuodolla, jonka tunnus on useimmiten pääte **-ks**:

<Interlinear hv="Melv|o val|o ondoso ipratt|aks." gloss="päärynä-NOM mies-GEN käsi-INS syödä-PRF.PASS.3SG" fi="Päärynän söi mies." />

Koko tekijän ilmauksen, <Hv glossed>valo ondoso</Hv> ’miehen toimesta’, voisi panna sulkeisiin. Ilman sitä lause on pelkkä <Hv glossed>Melvo iprattaks</Hv> ’päärynä syötiin’.

Passiivi on hyödyllinen, kun tekijää ei tiedetä tai sitä ei haluta mainita. Se auttaa myös kerronnassa: kun ketjutat lauseita, passiivin ansiosta subjektin ei tarvitse vaihtua kesken kaiken.

## Suomen passiivi on toista maata

Suomalaiselle sana *passiivi* on tuttu, mutta juuri siksi se voi johtaa harhaan. Suomen passiivi on persoonaton: *päärynä syötiin*, *minut nähtiin*, *meidät nähtiin*. Verbi ei taivu persoonan mukaan, ja kohde pysyy objektina (huomaa *minut*, akkusatiivi).

Valyrian passiivissa kohteesta tulee **oikea subjekti**. Se on nominatiivissa, ja verbi taipuu sen mukaan persoonassa ja luvussa. Siksi ’minut kannettiin’ on valyriaksi kirjaimellisesti *minä kannettiin*, nominatiivissa:

<Example id="ex-8b8dd815ed81" />

Toinen ero: suomessa passiiviin ei yleensä liitetä tekijää (*miehen toimesta* on kömpelöä virkakieltä). Kun suomalainen haluaa nostaa kohteen etualalle ja silti mainita tekijän, hän vaihtaa sanajärjestystä: *Päärynän söi mies.* Valyria tekee saman passiivilla, ja tekijän mainitseminen on aivan luontevaa. Siksi valyrian passiivi käännetään suomeksi usein juuri sanajärjestyksen avulla.

## Kolme päätettä

Passiivissa on kussakin aikamuodossa (infinitiivejä lukuun ottamatta) vain **kolme muotoa**, koska 1. ja 3. persoona ovat samat ja 2. persoona on sama yksikössä ja monikossa. Konsonanttivartaloisen verbin preesensissä ne ovat:

- yksikön 1. ja 3. persoona: <Hv>-aks</Hv>
- yksikön ja monikon 2. persoona (sinä ja te): <Hv>-āks</Hv>
- monikon 1. ja 3. persoona: <Hv>-aksi</Hv>

<Paradigm lemma="ipradagon" numbers="sg,pl" filter="indicative.passive.present" />

Siis <Hv glossed>ipradaks</Hv> ’minut syödään, hänet/se syödään’, <Hv glossed>ipradāks</Hv> ’sinut/teidät syödään’, <Hv glossed>ipradaksi</Hv> ’meidät/heidät syödään’. Pitkä vokaali erottaa 2. persoonan, ja suomalaisena kuulet eron vaivatta: *a* ja *ā* ovat sinulle eri äänteitä.

Vokaalivartaloisessa verbissä päätteen vokaalina on vartalon oma vokaali:

<Compare fi="nähd|ään" hv="urne|ks" label="passiivi ~ passiivi (1./3. yks.)" />

<Hv glossed>Urneks</Hv> ’minut/hänet nähdään’, <Hv glossed>urnēks</Hv> ’sinut/teidät nähdään’, <Hv glossed>urneksi</Hv> ’meidät/heidät nähdään’.

<Pronounce word="ipradakson" />

### Muissa aikamuodoissa

Perusmalli pysyy lähes kaikkialla samana.

**Subjunktiivissa** (tuttu kieltolauseista) vokaali vaihtuu, ja loppu-*i* muuttuu *y*:ksi kuten aina: <Hv>ipradoks</Hv>, <Hv>ipradōks</Hv>, <Hv>ipradoksy</Hv> ja <Hv>urnioks</Hv>, <Hv>urniōks</Hv>, <Hv>urnioksy</Hv>.

**Perfekti** toimii niin kuin odottaisit: perfektivartalo ja samat päätteet.

<Paradigm lemma="urnegon" numbers="sg,pl" filter="indicative.passive.perfect" />

Perfektin subjunktiivi: <Hv>iprattoks</Hv>, <Hv>iprattōks</Hv>, <Hv>iprattoksy</Hv>; <Hv>ūndoks</Hv>, <Hv>ūndōks</Hv>, <Hv>ūndoksy</Hv>.

<Line id="hotd-s1e10-007" />

<Line id="hotd-s2e07-003" />

**Futuuri** on epäsäännöllinen. Indikatiivin monikon 1. ja 3. persoonassa pääte on pelkkä <Hv>-iks</Hv>, ei *-aksi*:

<Paradigm lemma="ipradagon" numbers="sg,pl" filter="indicative.passive.future" />

Futuurin passiivin subjunktiivissa on sama *u*-vokaali kuin futuurin subjunktiivissa muutenkin: <Hv>ipradiluks</Hv>, <Hv>ipradilūks</Hv>, <Hv>ipradiluksy</Hv>; <Hv>urnīluks</Hv>, <Hv>urnīlūks</Hv>, <Hv>urnīluksy</Hv>.

Daemon käyttää futuurin passiivia, kun hän ilmoittaa menevänsä naimisiin. Subjekti on <Hv glossed>nyke</Hv> ’minä’, ja muoto on sama kuin 3. persoonassa:

<Line id="hotd-s1e02-012" />

Lisäksi on preesensin ja perfektin **passiivin infinitiivi**, <Hv>ipradakson</Hv> ja <Hv>iprattakson</Hv>. Myönteinen **käsky** annetaan passiivin 2. persoonan muodolla.

## Tekijä passiivilauseessa

Tekijän voi tuoda passiivilauseeseen kahdella tavalla.

**1. <Hv l="ondos">ondoso</Hv> ja genetiivi.** Tavallisin tapa on käyttää sanan <Hv glossed>ondos</Hv> ’käsi’ instrumentaalia postpositiona, joka vaatii genetiivin. Kirjaimellisesti se on ’miehen kädellä’, mutta kättä ei merkityksessä enää ajatella. Suomen *miehen toimesta* on rakenteeltaan yllättävän lähellä: sekin on genetiivi ja substantiivista syntynyt postpositio.

<Compare fi="miehe|n" hv="val|o" label="genetiivi ~ genetiivi" />

Kun tekijöitä on monta, myös postpositio on monikossa: <Hv l="ondossi">ondossi</Hv>.

<Interlinear hv="Taob|a val|oti ondossi ūnd|aks." gloss="poika-NOM mies-GEN.PL käsi-INS.PL nähdä-PRF.PASS.3SG" fi="Pojan näkivät miehet." />

<Example id="ex-53eea3e8d701" />

<Example id="ex-88132a27c24f" />

<Pronounce l="ondoso" word="ondoso" />

<Pronounce l="ondossi" word="ondossi" />

**2. Pelkkä genetiivi verbin edellä.** Jos tekijä on epämääräinen ja yleinen, se voi olla genetiivissä suoraan verbin edellä ilman postpositiota: <Hv glossed>Taoba valo ūndaks</Hv> ’pojan näki joku mies’.

Ero on merkityksessä. <Hv l="ondoso">Ondoso</Hv>-tekijä voi olla määräinen tai epämääräinen. Verbin edellä oleva pelkkä genetiivi on aina epämääräinen ja yleinen: *joku mies*, ei *se mies*.

<Example id="ex-848daf1f9434" />

Tässä <Hv>azanto</Hv> on pelkkä genetiivi verbin edellä: joku humalainen ritari, kukaan ei tiedä kuka.

<Note>
Suomessa ei ole tällaista kahden tekijärakenteen eroa. Englanti tekee eron artikkelilla (*by the man* ~ *by a man*), suomi asiayhteyden avulla. Valyriassa ero näkyy rakenteessa: kun tekijä on tietty, käytä <Hv l="ondoso">ondoso</Hv>-rakennetta.
</Note>

## Harjoituslauseita

<Practice hv="Melva iprattaksi." fi="Päärynät syötiin." />

<Practice hv="Melvo ipradilaks." fi="Päärynä tullaan syömään." />

<Practice hv="Riña valo ondoso ūndaks." fi="Tytön näki mies." />

<Practice hv="Taobi ābroti ondossi ūndaksi." fi="Naiset näkivät pojat." />

Lopuksi aktiivilause ja sama asia passiivissa. Äiti viipaloi päärynää:

<Example id="ex-842e348ef160" />

Kun päärynästä tehdään subjekti, äiti siirtyy <Hv l="ondoso">ondoso</Hv>-rakenteeseen ja verbi saa passiivin päätteen:

<Practice hv="Melvo ñuho muño ondoso nektoks." fi="Päärynää viipaloi äitini." />

## Muistettavaksi

- Passiivin tunnus on **-ks**. Kohteesta tulee subjekti (nominatiivi), ja verbi taipuu sen mukaan.
- Kolme muotoa: 1./3. yks. <Hv>-aks</Hv>, 2. <Hv>-āks</Hv>, 1./3. mon. <Hv>-aksi</Hv>. Futuurin monikossa pääte on <Hv>-iks</Hv>.
- Tekijä: genetiivi + <Hv l="ondoso">ondoso</Hv> (monikossa <Hv l="ondossi">ondossi</Hv>) tai pelkkä genetiivi verbin edellä, jolloin tekijä on aina epämääräinen.

<Vocab />

<Check />
